import { useCallback, useEffect, useState } from "react";
import {
  fetchEmailCatalog,
  fetchEmailPreview,
  sendTestEmail,
} from "../api";

const PREVIEW_DEBOUNCE_MS = 350;

export function useEmailTesting(token) {
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [emailType, setEmailType] = useState("");
  const [lang, setLang] = useState("hu");
  const [recipientName, setRecipientName] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [preview, setPreview] = useState({ subject: "", html: "" });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [sendLoading, setSendLoading] = useState(false);
  const [sendMessage, setSendMessage] = useState("");

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    async function loadCatalog() {
      setCatalogLoading(true);
      try {
        const data = await fetchEmailCatalog(token);
        if (cancelled) return;
        setCatalog(data.templates || []);
        const firstPreview =
          data.templates?.find((item) => item.previewSupported) ||
          data.templates?.[0];
        if (firstPreview) {
          setEmailType(firstPreview.id);
        }
      } catch {
        if (!cancelled) setPreviewError("Nem sikerült betölteni a sablonokat.");
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    }

    loadCatalog();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const loadPreview = useCallback(async () => {
    if (!token || !emailType) return;

    setPreviewLoading(true);
    setPreviewError("");

    try {
      const data = await fetchEmailPreview(token, {
        emailType,
        lang,
        recipientName,
      });
      setPreview({ subject: data.subject, html: data.html });
    } catch {
      setPreviewError("Az előnézet betöltése sikertelen.");
    } finally {
      setPreviewLoading(false);
    }
  }, [token, emailType, lang, recipientName]);

  useEffect(() => {
    if (!emailType) return;

    const timer = window.setTimeout(() => {
      loadPreview();
    }, PREVIEW_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [emailType, lang, recipientName, loadPreview]);

  const selectedTemplate = catalog.find((item) => item.id === emailType);

  async function handleSendTest() {
    if (!testEmail.trim()) {
      setSendMessage("Add meg a teszt e-mail címet.");
      return;
    }

    if (selectedTemplate && !selectedTemplate.sendSupported) {
      setSendMessage("Ehhez a sablonhoz csak előnézet érhető el.");
      return;
    }

    const confirmed = window.confirm(`Teszt e-mail küldése ide: ${testEmail}?`);
    if (!confirmed) return;

    setSendLoading(true);
    setSendMessage("");

    try {
      await sendTestEmail(token, {
        emailType,
        recipientEmail: testEmail.trim(),
        lang,
        recipientName,
      });
      setSendMessage("Teszt e-mail elküldve.");
    } catch {
      setSendMessage("Nem sikerült elküldeni a teszt e-mailt.");
    } finally {
      setSendLoading(false);
    }
  }

  const groupedCatalog = catalog.reduce((groups, item) => {
    const key = item.category || "Egyéb";
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
    return groups;
  }, {});

  return {
    catalog,
    catalogLoading,
    groupedCatalog,
    emailType,
    setEmailType,
    lang,
    setLang,
    recipientName,
    setRecipientName,
    testEmail,
    setTestEmail,
    preview,
    previewLoading,
    previewError,
    sendLoading,
    sendMessage,
    handleSendTest,
    selectedTemplate,
  };
}
